import {
  Box,
  Button,
  CircularProgress,
  Grid,
  InputLabel,
  makeStyles,
  TextField,
  useTheme,
  useMediaQuery,
  Typography
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { green } from '@material-ui/core/colors';
import { STATUS_API } from 'src/app/constant/config';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { selectServicePackage } from 'src/features/deviceSlice';
import CustomDialog from 'src/app/components/CustomDialog';
import { deviceType as deviceTypeList } from 'src/app/constant/deviceType';
import axiosInterceptor from 'src/app/axiosInterceptor';
import { showGlobalToast } from 'src/features/uiSlice';
import { messageToastType_const } from 'src/app/constant/config';

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined || amount === '') return '-';
  return Number(amount).toLocaleString('vi-VN') + ' ₫';
};

function EditDeviceModal({ open, onClose, device, devices, onSuccess }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const targetDevices = React.useMemo(() => {
    if (devices && devices.length > 0) return devices;
    return device ? [device] : [];
  }, [devices, device]);

  const targetDevice = targetDevices[0] || device;

  const [localPrices, setLocalPrices] = useState([]);
  const [loadingPrices, setLoadingPrices] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [description, setDescription] = useState('');

  const isAdmin = useSelector(state => state.authSlice.isAdmin);
  const agencyId =
    useSelector(state => state.authSlice.agencyId) ||
    targetDevice?.agency?.id ||
    targetDevice?.agency_id ||
    targetDevice?.owner_id;

  const targetDeviceTypeName =
    targetDevice?.deviceType?.name ||
    targetDevice?.device_type_name ||
    (typeof targetDevice?.device_type === 'string' ? targetDevice.device_type : undefined);

  const foundDeviceType = deviceTypeList.find(
    t => t.name && targetDeviceTypeName && t.name.toLowerCase() === targetDeviceTypeName.toLowerCase()
  );

  const targetDeviceTypeId =
    targetDevice?.deviceType?.id ||
    targetDevice?.device_type?.id ||
    targetDevice?.device_type_id ||
    (typeof targetDevice?.device_type === 'number' ? targetDevice.device_type : undefined) ||
    foundDeviceType?.id;

  // Fetch gói cước đại lý hoặc hệ thống
  useEffect(() => {
    if (!open) return;

    const fetchSystemPackages = () => {
      setLoadingPrices(true);
      axiosInterceptor
        .get(`/service_package/packages?device_type_id=${targetDeviceTypeId || ''}&version=${targetDevice?.hardware_version || ''}`)
        .then(res => {
          const data =
            res.data?.payload?.result ||
            res.data?.payload?.data ||
            res.data?.payload?.service_packages ||
            res.data?.payload?.servicePackages ||
            [];
          setLocalPrices(data);
        })
        .catch(() => setLocalPrices([]))
        .finally(() => setLoadingPrices(false));
    };

    if (isAdmin || !agencyId) {
      fetchSystemPackages();
    } else {
      setLoadingPrices(true);
      axiosInterceptor
        .get(`/service_package_price?owner_type=agency&owner_id=${agencyId}&page=1&page_size=100`)
        .then(res => {
          const data =
            res.data?.payload?.result ||
            res.data?.payload?.data ||
            res.data?.payload?.service_package_prices ||
            res.data?.payload?.servicePackagePrices ||
            [];
          if (data.length === 0) {
            // Fallback nếu đại lý chưa cấu hình gói
            fetchSystemPackages();
          } else {
            setLocalPrices(data);
          }
        })
        .catch(() => {
          fetchSystemPackages();
        })
        .finally(() => setLoadingPrices(false));
    }
  }, [open, agencyId, isAdmin, targetDeviceTypeId, targetDevice]);

  const customPricesForDeviceType = localPrices.filter(item => {
    const pkg = item.service_package || item.servicePackage || item;
    const pkgName = pkg?.name || '';
    const isActivation = pkg?.type ? pkg.type === 'KICH_HOAT' : /kích hoạt|active|kh/i.test(pkgName);
    if (isActivation) return false;

    // 1. So khớp theo tên loại thiết bị
    const pkgDeviceTypeName = pkg?.deviceType?.name || pkg?.device_type?.name || pkg?.device_type_name;
    if (pkgDeviceTypeName && targetDeviceTypeName) {
      return pkgDeviceTypeName.toLowerCase() === targetDeviceTypeName.toLowerCase();
    }

    // 2. Fallback so khớp theo ID
    const deviceTypeId =
      pkg?.device_type_id ||
      pkg?.device_type?.id ||
      pkg?.deviceType?.id ||
      (typeof pkg?.device_type === 'number' ? pkg.device_type : undefined) ||
      (typeof pkg?.deviceType === 'number' ? pkg.deviceType : undefined);
    return Number(deviceTypeId) === Number(targetDeviceTypeId);
  });

  // Hiển thị các gói đại lý đã lọc theo loại thiết bị
  const serviceOption = customPricesForDeviceType.map(item => {
    const pkg = item.service_package || item.servicePackage || item;
    const price = item.price ?? pkg.cost;
    return {
      id: Number(pkg.id),
      priceRecordId: item.price ? item.id : null, // Chỉ dùng priceRecordId nếu đây thực sự là record giá của agency
      cost: price,
      name: `${pkg.name} (${formatCurrency(price)})`,
      description: pkg.description || ''
    };
  });

  const handleSubmit = async data => {
    const selectedPkgId = Number(data.service_package_id);
    const selectedOpt = serviceOption.find(opt => opt.id === selectedPkgId);
    
    setSubmitting(true);
    try {
      const devicesIds = targetDevices.map(d => Number(d.id));
      
      // Nếu có cấu hình giá riêng (priceRecordId) và đang khai báo cho đại lý
      if (selectedOpt && selectedOpt.priceRecordId) {
        // Sử dụng API mới: POST /service_package_price/declare-devices
        await axiosInterceptor.post('/service_package_price/declare-devices', {
          devicesIds: devicesIds,
          service_package_price_id: selectedOpt.priceRecordId
        });
        dispatch(
          showGlobalToast({
            message: 'Khai báo gói dịch vụ thành công!',
            type: messageToastType_const.success
          })
        );
      } else {
        // Fallback: Gọi selectServicePackage cho từng thiết bị
        for (const devId of devicesIds) {
          await dispatch(
            selectServicePackage({
              data: { service_package_id: selectedPkgId },
              id: devId
            })
          ).unwrap();
        }
        dispatch(
          showGlobalToast({
            message: 'Khai báo gói dịch vụ thành công!',
            type: messageToastType_const.success
          })
        );
      }
      if (onSuccess) onSuccess();
      onClose();
      setDescription('');
    } catch (err) {
      console.error(err);
      dispatch(
        showGlobalToast({
          message: err?.response?.data?.message || err?.message || 'Khai báo gói dịch vụ thất bại!',
          type: messageToastType_const.error
        })
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
    setDescription('');
  };

  const handleSelectPackage = (event, value, setFieldValue) => {
    const selectedPackage = serviceOption.find(
      (pkg) => pkg.id === Number(value.id)
    );
    if (selectedPackage) {
      setDescription(selectedPackage.description);
    }
    setFieldValue('service_package_id', Number(value.id));
  }

  return (
    <CustomDialog title="Chọn gói dịch vụ gia hạn" open={open} onClose={handleClose}>
      <div>
        <Formik
          initialValues={{
            serial: device.serial,
            version: device.hardware_version,
            service_package_id: '',
            description: device?.description || ''
          }}
          enableReinitialize={true}
          validationSchema={Yup.object().shape({
            service_package_id: Yup.string()
              .max(100)
              .required('Vui lòng chọn gói dịch vụ gia hạn')
          })}
          onSubmit={handleSubmit}
        >
          {({
            errors,
            handleBlur,
            handleChange,
            handleSubmit,
            touched,
            values,
            setFieldValue
          }) => (
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                {targetDevices.length > 1 ? (
                  <Grid item xs={12}>
                    <Box
                      p={1.5}
                      style={{
                        maxHeight: '120px',
                        overflowY: 'auto',
                        border: '1px solid rgba(0, 0, 0, 0.12)',
                        borderRadius: '4px',
                        backgroundColor: '#fafafa'
                      }}
                    >
                      <Typography variant="body2" style={{ fontWeight: 600, marginBottom: '6px' }}>
                        Danh sách Serial thiết bị ({targetDevices.length}):
                      </Typography>
                      <Grid container spacing={1}>
                        {targetDevices.map(d => (
                          <Grid item xs={6} sm={4} key={d.id}>
                            <span style={{ fontSize: '13px', fontFamily: 'monospace', color: '#555' }}>
                              • {d.serial || d.license_plate || `ID: ${d.id}`}
                            </span>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  </Grid>
                ) : (
                  <>
                    <Grid item md={6} xs={12}>
                      <InputLabel>
                        Serial thiết bị <span style={{ color: 'red' }}></span>
                      </InputLabel>
                      <TextField
                        error={Boolean(touched.email && errors.email)}
                        fullWidth
                        disabled={true}
                        helperText={touched.email && errors.email}
                        margin="normal"
                        className="input-no-fieldset"
                        name="email"
                        size="small"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.serial}
                        variant="outlined"
                      />
                    </Grid>

                    <Grid item md={6} xs={12}>
                      <InputLabel>
                        Phiên bản <span style={{ color: 'red' }}></span>
                      </InputLabel>
                      <TextField
                        error={Boolean(touched.version && errors.version)}
                        className="input-no-fieldset"
                        fullWidth
                        disabled={true}
                        helperText={touched.version && errors.version}
                        margin="normal"
                        name="description"
                        size="small"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.version}
                        variant="outlined"
                      />
                    </Grid>
                  </>
                )}

                <Grid item md={6} xs={12}>
                  <InputLabel style={{ marginBottom: "21px" }}>
                    Gói dịch vụ gia hạn <span className="text-danger">*</span>
                  </InputLabel>
                  <Autocomplete
                    labelId="demo-simple-select-outlined-label"
                    id="demo-simple-select-outlined"
                    size="small"
                    options={serviceOption || []}
                    getOptionLabel={option => option.name}
                    value={(serviceOption || []).find(
                      option => option.id === Number(values.service_package_id)
                    )}
                    onChange={(e, value) => handleSelectPackage(e, value, setFieldValue)}
                    disableClearable
                    renderInput={params => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder="Chọn gói dịch vụ"
                        name="service_package_id"
                        error={Boolean(
                          touched.service_package_id && errors.service_package_id
                        )}
                        helperText={
                          touched.service_package_id && errors.service_package_id
                        }
                        InputProps={{
                          ...params.InputProps,
                          style: {
                            fontSize: '16px',
                            padding: '0 8px',
                            height: '35px',
                          }
                        }}
                      />
                    )}
                    className={classes.customOutline}
                  />
                </Grid>

                <Grid item md={6} xs={12}>
                  <InputLabel>
                    Mô tả <span style={{ color: 'red' }}></span>
                  </InputLabel>
                  <TextField
                    fullWidth
                    disabled={true}
                    margin="normal"
                    className="input-no-fieldset"
                    name="email"
                    size="small"
                    onBlur={handleBlur}
                    value={description}
                    variant="outlined"
                  />
                </Grid>

              </Grid>
              <Box my={3}>
                <div className={classes.groupButtonSubmit}>
                  <div className={classes.wrapper}>
                    <Button
                      style={{
                        ...(isMobile && { maxWidth: 90 }),
                        marginRight: '10px',
                        textTranform: 'none !important'
                      }}
                      onClick={() => onClose()}
                      className="btn-main btn-plain mx-3"
                      color="primary"
                      size="large"
                      variant="contained"
                    >
                      Thoát
                    </Button>
                    <Button
                      style={{
                        ...(isMobile && { maxWidth: 120 }),
                        marginRight: '10px',
                        textTranform: 'none !important'
                      }}
                      className="btn-main mx-3"
                      color="primary"
                      size="large"
                      type="submit"
                      variant="contained"
                    >
                      Lưu lại
                    </Button>
                    {submitting && (
                      <CircularProgress
                        size={24}
                        className={classes.buttonProgress}
                      />
                    )}
                  </div>
                </div>
              </Box>
            </form>
          )}
        </Formik>
      </div>
    </CustomDialog>
  );
}

const useStyles = makeStyles(theme => ({
  root: {
    minHeight: '100%',
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(3)
  },
  groupButtonSubmit: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: '15px',

    '& .left-button': {
      display: 'flex'
    }
  },
  wrapper: {
    position: 'relative'
  },
  buttonProgress: {
    color: green[500],
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12
  },
  customOutline: {
    '& .MuiOutlinedInput-notchedOutline': {
      borderWidth: '1px',
      borderColor: 'rgba(31, 31, 31, 0.14)',
      borderRadius: '4px'
    }
  }
}));

export default React.memo(EditDeviceModal);
